import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import EventCard from '@/components/EventCard';
import BuyTicketsModal from '@/components/BuyTicketsModal';
import { EVENTS, TICKETS } from '@/constants/testIds';
import { Loader2, Calendar, MapPin, Ticket } from 'lucide-react';

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/events').then(({ data }) => setEvents(data.events)).finally(() => setLoading(false));
  }, []);
  return (
    <div className="pt-28 pb-24 max-w-7xl mx-auto px-6 sm:px-12">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-3">What's on</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">Upcoming <span className="rainbow-text">events</span></h1>
        <p className="text-slate-400 max-w-xl mx-auto">Concerts, festivals, workshops and fan meetups.</p>
      </div>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div> :
        <div className="grid lg:grid-cols-2 gap-5">{events.map((e) => <EventCard key={e.event_id} event={e} />)}</div>
      }
    </div>
  );
};

export const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  useEffect(() => { api.get(`/events/${id}`).then(({ data }) => setEvent(data.event)); }, [id]);
  if (!event) return <div className="pt-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div>;
  return (
    <div data-testid={EVENTS.detailRoot} className="pb-24">
      <div className="relative h-[55vh] min-h-[400px] overflow-hidden">
        <img src={event.cover_image} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1020] via-[#0B1020]/60 to-[#0B1020]/20" />
      </div>
      <div className="max-w-4xl mx-auto px-6 -mt-32 relative z-10">
        <div className="glass-strong rounded-3xl p-10 border border-white/10">
          <div className="text-xs uppercase tracking-widest text-cyan-300 mb-3">{event.category || 'Event'}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-5">{event.title}</h1>
          <div className="flex flex-wrap gap-5 text-sm text-slate-300 mb-6">
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-fuchsia-300" />{event.event_date}</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-cyan-300" />{event.venue}, {event.city}</span>
            <span className="flex items-center gap-2"><Ticket className="h-4 w-4 text-amber-300" />{event.ticket_price ? `₹${event.ticket_price.toLocaleString('en-IN')}` : 'Free'}</span>
          </div>
          <p className="text-slate-300 leading-relaxed">{event.description}</p>
          <button
            data-testid={TICKETS.buyButton}
            onClick={() => setBuyOpen(true)}
            disabled={!event.ticket_price}
            className="mt-8 btn-rainbow rounded-full px-7 py-3 text-white disabled:opacity-50"
          >
            {event.ticket_price ? 'Buy tickets' : 'Free entry'}
          </button>
        </div>
      </div>
      <BuyTicketsModal event={event} open={buyOpen} onOpenChange={setBuyOpen} />
    </div>
  );
};
